import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {AbstractMerchantSupplierComponent} from 'app/core/common/abstract.merchant-supplier.component';
import {Event} from 'app/core/models/event';
import {EventService} from 'app/core/services/event.service';
import {MerchantService} from 'app/core/services/merchant.service';
import {NotifMessageService} from 'app/core/services/notifmessage.service';
import {PosService} from 'app/core/services/pos.service';
import {SupplierService} from 'app/core/services/supplier.service';
import {NGXLogger} from 'ngx-logger';
import {ReplaySubject, Subscription} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {AuthenticatedRouteGuard} from 'security/authenticatedRouteGuard.service';
import {KeycloakService} from 'security/keycloak.service';
import {tableEventChange} from '../table-utils/classes/tableChangeEvent';
import {columnType} from '../table-utils/enums/columnType';


@Component({
    selector: 'app-alerts-page',
    templateUrl: './alerts-page.component.html'
})
export class AlertsPageComponent extends AbstractMerchantSupplierComponent implements OnInit, OnDestroy {

    dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
    totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

    isLoadingResults: boolean = true;
    isSearching: boolean;

    alerts: Map<number, Event> = new Map<number, Event>();

    columns: {
        data: {
            title: string;
            type: columnType
        }[],
        clickable: boolean;
    };

    pager: {
        page: number,
        sort: string,
        size: number
    } = {
        page: 0,
        sort: "createdDate,DESC",
        size: 20
    };

    criteria: Event = new Event();

    columnPropertyMapping = {
        "CREATION_DATE": "createdDate",
        "ALERT_TYPE": "eventType",
        "ALERT_SOURCE": "eventSource",
        "CORRELATION_ID": "correlationId"
    };

    searchForm: FormGroup = new FormGroup({
        from: new FormControl(),
        to: new FormControl(),
        eventType: new FormControl(),
        correlationId: new FormControl()
    })

    modalResponseSubscription: Subscription;
    allAlerts: Array<String> = Array<String>();
    alertTypesMap: Array<{ element: String, traduction: String }> = new Array<{ element: String, traduction: String }>();

    constructor(
        private eventService: EventService,
        protected supplierService: SupplierService,
        protected merchantService: MerchantService,
        protected posService: PosService,
        private translate: TranslateService,
        private notifService: NotifMessageService,
        protected log: NGXLogger,
        public router: Router,
        private keycloakService: KeycloakService,
        private roleGuard: AuthenticatedRouteGuard
    ) {
        super(supplierService, merchantService, posService, log, router);
    }


    ngOnInit() {
        this.columns = {
            data: [
                {title: "CREATION_DATE", type: columnType.date},
                {title: "ALERT_TYPE", type: columnType.translateText},
                {title: "ALERT_SOURCE", type: columnType.text},
                {title: "CORRELATION_ID", type: columnType.text}
            ],
            clickable: true
        };

        this.criteria.eventCategory = "ALERT_EVENT";
        this.allAlerts = this.getAlertTypes();
        this.sortAlertType();

        if (this.allAlerts != null) {
            this.translate.onLangChange.subscribe(() => {
                this.sortAlertType();
            });
        }

        this.keycloakService.rolesObservable.subscribe(roles => {
            if (roles.length !== 0) {
                if (this.roleGuard.isEnabled('EVENT_READ')) {
                    this.loadData();
                }
            }
        });
    }

    ngOnDestroy() {
        super.closeSubscribtions();
    }

    formatAlert(alert: Event) {
        this.alerts.set(alert.id, alert)
        return {
            id: alert.id,
            CREATION_DATE: alert.createdDate,
            ALERT_TYPE: alert.eventType,
            ALERT_SOURCE: alert.eventSource,
            CORRELATION_ID: alert.correlationId
        };
    }

    loadData(isLoadingSearch: boolean = false): void {
        this.isLoadingResults = true;
        this.eventService
            .findByCriteria(this.criteria, this.pager)
            .pipe(
                tap(alerts => this.totalLength$.next(alerts.totalElements)),
                map(alerts => {
                    if (!alerts) {
                        this.isLoadingResults = false;
                        return [];
                    }
                    if (alerts.content.length === 0) {
                        this.isLoadingResults = false;
                        if (isLoadingSearch) {
                            this.notifService.notifyInfo('EMPTY_RESULT');
                        }
                        return [];
                    }
                    if (alerts.content.length !== 0) {
                        this.isLoadingResults = false;
                        return alerts.content.map(error => this.formatAlert(error));
                    }
                }),
            ).subscribe(errors => {
            this.dataSubject$.next(errors);
        })
    }

    tableChange(event: tableEventChange) {
        const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
        const sortDirection = event.sortEvent.direction.toUpperCase();
        this.pager = {
            page: event.pageIndex,
            sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "createdDate,DESC",
            size: event.pageSize
        }
        this.loadData();
    }

    search(): void {
        this.criteria = new Event();
        this.criteria.eventCategory = "ALERT_EVENT";
        for (let field in this.searchForm.value) {
            if (!this.searchForm.value[field]) {
                delete this.searchForm.value[field];
            } else {
                if (field === "eventType") {
                    let trad = this.alertTypesMap.find(Type => Type.traduction == this.searchForm.value[field]);
                    this.criteria["eventType"] = (typeof trad !== "undefined") ? trad.element : this.searchForm.value[field];
                } else if (field === "from" || field === "to") {
                    const fieldValue = this.searchForm.value[field];
                    const date = new Date(fieldValue);
                    date.setSeconds(0, 0);
                    this.criteria[field] = date.toISOString();
                } else {
                    this.criteria[field] = this.searchForm.value[field]
                }
            }
        }
        if (this.criteria && this.criteria["from"] && this.criteria["to"] && this.criteria["from"] > this.criteria["to"]) {
            this.notifService.notifyErrorWithoutDetail("ERROR_DATE");
            return;
        }
        this.loadData(true);
    }

    details(alertId: number): void {
        const alert = this.alerts.get(alertId);
        this.notifService.emitModalWithDetails("ALERT", alert);
    }

    clearSearch(): void {
        this.searchForm.reset();
        this.criteria = new Event();
        this.criteria.eventCategory = "ALERT_EVENT";
        this.loadData();
    }

    sortAlertType() {
        const allAlertTypes = [];
        this.getAlertTypes().forEach(element =>
            this.translate.get(element).subscribe((trad: string) => {
                allAlertTypes.push(trad);
                this.alertTypesMap.push({element: element, traduction: trad});
                allAlertTypes.sort((a: string, b: string) => {
                    return this.translate.instant(a).localeCompare(this.translate.instant(b))
                });
            }));
        this.allAlerts = allAlertTypes;
    }

}
